import type { ApiError } from '../../api/client'
import { workoutApi } from '../../api/workoutApi'
import type {
  WorkoutExerciseLog,
  WorkoutSession,
  WorkoutSetLog,
} from '../../api/workoutApi'
import {
  isLogActualDirty,
  logActualFromExercise,
  normalizeSetLogsForSave,
  type LogActuals,
} from './workoutLogActuals'

export const WORKOUT_STALE_LOG = 'workout.log.stale'
export const WORKOUT_STALE_SESSION = 'workout.session.stale'

export interface WorkoutSessionMutations {
  getSession: (sessionId: number) => Promise<WorkoutSession>
  setCurrentExercise: (
    sessionId: number,
    logId: number,
    expectedSessionUpdatedAt: string | null,
  ) => Promise<WorkoutSession>
  updateExerciseLog: (
    sessionId: number,
    logId: number,
    comment: string | null,
    setLogs: WorkoutSetLog[],
    expectedUpdatedAt: string | null,
  ) => Promise<WorkoutExerciseLog>
}

export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'status' in err && 'message' in err
}

export function isStaleLogError(err: unknown): boolean {
  return isApiError(err) && err.status === 409 && err.message === WORKOUT_STALE_LOG
}

export function isStaleSessionError(err: unknown): boolean {
  return isApiError(err) && err.status === 409 && err.message === WORKOUT_STALE_SESSION
}

export function dirtyLogIdsFromActuals(
  exercises: WorkoutExerciseLog[],
  actuals: LogActuals,
): Set<number> {
  const dirty = new Set<number>()
  for (const ex of exercises) {
    if (isLogActualDirty(ex, actuals[ex.id])) dirty.add(ex.id)
  }
  return dirty
}

/** Server wins for logs that are not dirty locally. Dirty logs keep local values. */
export function mergeServerSession(
  local: WorkoutSession | null,
  server: WorkoutSession,
  dirtyLogIds: Set<number>,
): WorkoutSession {
  if (!local || local.id !== server.id) return server
  const localById = new Map(local.exercises.map(ex => [ex.id, ex]))
  return {
    ...server,
    exercises: server.exercises.map(serverEx =>
      dirtyLogIds.has(serverEx.id) ? (localById.get(serverEx.id) ?? serverEx) : serverEx,
    ),
  }
}

export function mergeActualsPreservingDirty(
  session: WorkoutSession,
  prevActuals: LogActuals,
  dirtyLogIds: Set<number>,
  logIds?: Iterable<number>,
): LogActuals {
  const ids = logIds ? [...logIds] : session.exercises.map(ex => ex.id)
  const byId = new Map(session.exercises.map(ex => [ex.id, ex]))
  const next: LogActuals = {}
  for (const id of ids) {
    const ex = byId.get(id)
    if (!ex) continue
    if (dirtyLogIds.has(id) && prevActuals[id]) {
      next[id] = prevActuals[id]
    } else {
      next[id] = logActualFromExercise(ex)
    }
  }
  return next
}

export function applyUpdatedLog(
  session: WorkoutSession,
  updated: WorkoutExerciseLog,
): WorkoutSession {
  return {
    ...session,
    exercises: session.exercises.map(ex => (ex.id === updated.id ? { ...ex, ...updated } : ex)),
  }
}

export type SaveDirtyLogsResult =
  | { ok: true; session: WorkoutSession }
  | { ok: false; stale: 'log'; session: WorkoutSession; remainingDirty: Set<number> }

export async function saveDirtyLogs(
  api: WorkoutSessionMutations,
  session: WorkoutSession,
  logs: WorkoutExerciseLog[],
  actuals: LogActuals,
): Promise<SaveDirtyLogsResult> {
  let next = session
  for (const original of logs) {
    const ex = next.exercises.find(e => e.id === original.id) ?? original
    if (!isLogActualDirty(ex, actuals[ex.id])) continue
    const row = actuals[ex.id] ?? logActualFromExercise(ex)
    try {
      const updated = await api.updateExerciseLog(
        next.id,
        ex.id,
        row.comment.trim() || null,
        normalizeSetLogsForSave(row.setLogs),
        ex.updatedAt ?? null,
      )
      next = applyUpdatedLog(next, updated)
    } catch (err) {
      if (isStaleLogError(err)) {
        const server = await api.getSession(next.id)
        const remainingDirty = new Set(
          logs
            .filter(l => l.id !== ex.id)
            .filter(l => {
              const current = next.exercises.find(e => e.id === l.id) ?? l
              return isLogActualDirty(current, actuals[l.id])
            })
            .map(l => l.id),
        )
        return {
          ok: false,
          stale: 'log',
          session: mergeServerSession(next, server, remainingDirty),
          remainingDirty,
        }
      }
      throw err
    }
  }
  return { ok: true, session: next }
}

export type SetCurrentResult = {
  session: WorkoutSession
  staleSession: boolean
}

export async function refreshAndSetCurrent(
  api: WorkoutSessionMutations,
  session: WorkoutSession,
  targetLogId: number,
  dirtyLogIds: Set<number>,
): Promise<SetCurrentResult> {
  const server = await api.getSession(session.id)
  const merged = mergeServerSession(session, server, dirtyLogIds)
  try {
    const updated = await api.setCurrentExercise(
      merged.id,
      targetLogId,
      merged.updatedAt ?? null,
    )
    return { session: mergeServerSession(merged, updated, dirtyLogIds), staleSession: false }
  } catch (err) {
    if (isStaleSessionError(err)) {
      const latest = await api.getSession(session.id)
      return { session: mergeServerSession(merged, latest, dirtyLogIds), staleSession: true }
    }
    throw err
  }
}

export const traineeWorkoutMutations: WorkoutSessionMutations = {
  getSession: sessionId => workoutApi.getSession(sessionId),
  setCurrentExercise: (sessionId, logId, expected) =>
    workoutApi.setCurrentExercise(sessionId, logId, expected),
  updateExerciseLog: (sessionId, logId, comment, setLogs, expected) =>
    workoutApi.updateExerciseLog(sessionId, logId, comment, setLogs, expected),
}

export const coachWorkoutMutations: WorkoutSessionMutations = {
  getSession: sessionId => workoutApi.coachGetSession(sessionId),
  setCurrentExercise: (sessionId, logId, expected) =>
    workoutApi.coachSetCurrentExercise(sessionId, logId, expected),
  updateExerciseLog: (sessionId, logId, comment, setLogs, expected) =>
    workoutApi.coachUpdateExerciseLog(sessionId, logId, comment, setLogs, expected),
}
